import { useState } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { initialsOf } from './outcomeMeasure';

export const PORTRAIT_SIZE = 36;

interface OutcomePortraitProps {
    outcomeId: string;
    label: string;
    image: string | null;
    /** The outcome's colour on the chart; null when it is not charted. */
    color: string | null;
}

/**
 * 36 px disc with a 2 px ring in the outcome's chart colour (it replaces the
 * desktop row's colour square). The outcome's image fills it; with no image,
 * or one that does not load, it shows the initials.
 */
export function OutcomePortrait({
    outcomeId,
    label,
    image,
    color,
}: OutcomePortraitProps) {
    // The address that failed, so a new image for the same outcome is tried.
    const [failed, setFailed] = useState<string | null>(null);
    const src = image !== null && image !== failed ? image : null;

    return (
        <Box
            component="span"
            aria-hidden="true"
            data-testid={`outcome-portrait-${outcomeId}`}
            sx={{
                width: PORTRAIT_SIZE,
                height: PORTRAIT_SIZE,
                flexShrink: 0,
                boxSizing: 'border-box',
                borderRadius: '50%',
                border: '2px solid',
                borderColor: color ?? (t => alpha(t.palette.text.primary, 0.2)),
                bgcolor: 'action.selected',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                fontSize: '12px',
                fontWeight: 500,
                lineHeight: 'normal',
                color: 'text.secondary',
            }}
        >
            {src ? (
                <Box
                    component="img"
                    src={src}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    onError={() => setFailed(src)}
                    sx={{
                        display: 'block',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                />
            ) : (
                initialsOf(label)
            )}
        </Box>
    );
}
